import { useState } from 'react';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { deserialize } from 'serializr';
import { EmissionResponse } from 'models/MainDashboard/emissionModel.model';
import { SourceCO2eResponse } from 'models/MainDashboard/emissionModelSource.model';
import { YoYEmissionsProductResponse } from 'models/MainDashboard/yoyModel.model';
import { EmissionByLocationResponse } from 'models/MainDashboard/locationDashboard.model';
import { BLOB_TYPE } from 'views/CompanyDashboard/constants';
import { handleBlobDownload } from 'shared/utils/handleBlobDownload';
import { applyEmissionSourceDisplayLabels } from 'shared/utils/emissionSourceDisplayLabel';
import { LocationTableFields } from 'enums/CompanySetupEnums';
import { SbtiDashboardResponse } from 'models/Sbti/sbtiYoy.model';
import { generatePath } from 'react-router-dom';

const EmissionsService = () => {
  const [pieloading, setPieLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [yearEmissionLoading, setYearEmissionLoading] = useState<boolean>(false);
  const [emissionScopeChartData, setEmissionScopeChartData] = useState<EmissionResponse>();
  const [emissionSourceChartData, setEmissionSourceChartData] = useState<SourceCO2eResponse>();
  const [locationData, setLocationData] = useState<EmissionByLocationResponse>();
  const [yoyData, setYoyData] = useState<SbtiDashboardResponse>();
  const [yoyProductData, setYoyProductData] = useState<YoYEmissionsProductResponse>();

  const fetchEmissionData = async (params: {
    year?: number;
    organizationId?: number;
    locationId?: number;
    exportType?: string;
  }) => {
    try {
      setPieLoading(true);
      const { year, organizationId, locationId, exportType } = params;

      const requestParams = {
        year,
        organization_id: organizationId,
        location_id: locationId,
        export_type: exportType,
      };

      if (exportType) {
        const response = await axiosInstance.get(ApiRoutes.EMISSION_SCOPE, {
          responseType: BLOB_TYPE,
          params: requestParams,
        });
        handleBlobDownload(response);
      } else {
        const response = await axiosInstance.get(ApiRoutes.EMISSION_SCOPE, {
          params: requestParams,
        });
        const emissionResponse = deserialize(EmissionResponse, response.data);
        setEmissionScopeChartData(emissionResponse);
      }
    } catch (error) {
    } finally {
      setPieLoading(false);
    }
  };

  const fetchEmissionDataSource = async (params: {
    year?: number;
    organizationId?: number;
    scopeCategory?: string;
    locationId?: number;
    exportType?: string;
  }) => {
    try {
      setPieLoading(true);
      const { year, organizationId, scopeCategory, locationId, exportType } = params;

      const requestParams = {
        year,
        organization_id: organizationId,
        scope_category: scopeCategory,
        location_id: locationId,
        export_type: exportType,
      };

      if (exportType) {
        const response = await axiosInstance.get(ApiRoutes.EMISSION_SOURCE, {
          responseType: BLOB_TYPE,
          params: requestParams,
        });

        handleBlobDownload(response);
      } else {
        const response = await axiosInstance.get(ApiRoutes.EMISSION_SOURCE, {
          params: requestParams,
        });
        const emissionResponse = deserialize(SourceCO2eResponse, response.data);
        applyEmissionSourceDisplayLabels(emissionResponse.sourceCO2e);
        setEmissionSourceChartData(emissionResponse);
      }
    } catch (error) {
    } finally {
      setPieLoading(false);
    }
  };

  const fetchEmissionByLocation = async (params: {
    year: number;
    organizationId: number;
    locationId?: number;
    page?: number;
    sortKey?: LocationTableFields.ASC | LocationTableFields.DESC;
    exportType?: string;
  }) => {
    const {
      year,
      organizationId,
      locationId,
      page = 1,
      sortKey = LocationTableFields.DESC,
      exportType,
    } = params;

    const queryParams = {
      year,
      organization_id: organizationId,
      location_id: locationId,
      page,
      sort_order: sortKey,
      export_type: exportType,
    };

    try {
      setLoading(true);
      if (exportType) {
        const response = await axiosInstance.get(
          ApiRoutes.EMISSION_BY_LOCATION,
          { responseType: BLOB_TYPE, params: queryParams }
        );
        handleBlobDownload(response);
      } else {
        const response = await axiosInstance.get(
          ApiRoutes.EMISSION_BY_LOCATION,
          { params: queryParams }
        );
        const emissionResponse = deserialize(
          EmissionByLocationResponse,
          response.data
        );

        setLocationData(emissionResponse);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchYoYEmissions = async (params: {
    organizationId: number;
    locationId?: number;
    targetName?: string;
    year?: number;
    exportType?: string;
  }) => {
    const { targetName, organizationId, locationId, exportType, year } = params;

    try {
      setYearEmissionLoading(true)
      const queryParams = {
        organization_id: organizationId,
        location_id: locationId,
        year,
        target_name: targetName,
        export_type: exportType,
      };

      if (exportType) {
        const response = await axiosInstance.get(ApiRoutes.YOY_EMISSIONS, {
          responseType: BLOB_TYPE,
          params: queryParams,
        });
        handleBlobDownload(response);
      } else {
        const response = await axiosInstance.get(ApiRoutes.YOY_EMISSIONS, {
          params: queryParams,
        });
        const emissionResponse = deserialize(
          SbtiDashboardResponse,
          response.data
        );

        setYoyData(emissionResponse);
      }
    } catch (error) {}
    finally{
      setYearEmissionLoading(false)
    }
  };

  const fetchYoYProductEmissions = async (params: {
    productId: number;
    exportType?: string;
  }) => {
    const { productId, exportType } = params;

    try {
      const queryParams = {
        export_type: exportType,
      };
      const url = generatePath(ApiRoutes.YOY_PROD_EMISSION, { productId });
      if (exportType) {
        const response = await axiosInstance.get(url, {
          responseType: BLOB_TYPE,
          params: queryParams,
        });
        handleBlobDownload(response);
      } else {
        const response = await axiosInstance.get(url, {
          params: queryParams,
        });

        const emissionResponse = deserialize(
          YoYEmissionsProductResponse,
          response.data
        );

        setYoyProductData(emissionResponse);
      }
    } catch (error) {}
  };

  return {
    loading,
    pieloading,
    yearEmissionLoading,
    emissionScopeChartData,
    emissionSourceChartData,
    locationData,
    yoyData,
    yoyProductData,
    fetchEmissionData,
    fetchEmissionDataSource,
    fetchEmissionByLocation,
    fetchYoYEmissions,
    fetchYoYProductEmissions,
  };
};

export default EmissionsService; 